import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import Layout from '../Components/Layout';
import 'react-toastify/dist/ReactToastify.css';

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingUser, setEditingUser] = useState(null);
    const [deletingUser, setDeletingUser] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        username: '',
        role_id: '',
        password: ''
    });
    const [newUserData, setNewUserData] = useState({
        firstname: '',
        lastname: '',
        username: '',
        role_id: '',
        password: ''
    });

    useEffect(() => {
        IsAdmin();
        fetchUsers();
        fetchRoles();
    }, []);
    
    const IsAdmin = async () => {
        try {
            const response = await axios.get('manage/check-role');
            if(response.data !== "Admin") {
                window.location.href = "/login";
            }
        } catch (error) {
            window.location.href = "/login";
        }
    }

    // Fetch all users
    const fetchUsers = async () => {
        try {
            const response = await axios.get('manage/users');
            const transformedUsers = response.data.map(user => ({
                id: user.id,
                firstname: user.firstname,
                lastname: user.lastname,
                username: user.username,
                role_id: user.role_id,
                role_name: user.role_name
            }));
            setUsers(transformedUsers);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to fetch users');
            setLoading(false);
        }
    };

    // Fetch all roles
    const fetchRoles = async () => {
        try {
            const response = await axios.get('manage/roles');
            setRoles(response.data);
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Failed to fetch roles');
        }
    };

    // Handle edit click
    const handleEdit = (user) => {
        setEditingUser(user.id);
        setFormData({
            firstname: user.firstname,
            lastname: user.lastname,
            username: user.username,
            role_id: user.role_id,
            password: ''
        });
    };

    // Handle update
    const handleUpdate = async (userId) => {
        try {
            const updateData = {
                ...formData,
                id: userId
            };
            
            if (!formData.password) {
                delete updateData.password;
            }

            await axios.put(`manage/users/${userId}`, updateData);
            toast.success('User updated successfully');
            setEditingUser(null);
            fetchUsers();
        } catch (error) {
            console.error('Error updating user:', error);
            toast.error('Failed to update user');
        }
    };

    // Handle delete
    const handleDelete = async (userId) => {
        try {
            await axios.delete(`manage/users/${userId}`);
            toast.success('User deleted successfully');
            setDeletingUser(null);
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user');
        }
    };

    // Handle add new user
    const handleAddUser = async () => {
        axios.post(`auth/`, newUserData, {headers: { 'Content-Type': 'application/json' }})
            .then(response => {
                if (response.data.userCreated === 201) {
                    toast.success('User added successfully');
                    setShowAddForm(false);
                    setNewUserData({
                        firstname: '',
                        lastname: '',
                        username: '',
                        role_id: '',
                        password: ''
                    });
                    fetchUsers();
                } else if (response.data.userCreated === 409) {
                    toast.success('User/Account Already Exists!');
                } else {
                    toast.error('Something went wrong, try again later');
                }
            }).catch(error => {
            toast.error('There was an error!', error);
        });

    };

    // Filter users based on search
    const filteredUsers = users.filter(user => 
        user.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.role_name && user.role_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <Layout>
            <div style={{ height: 'calc(100vh - 70px)', overflow: 'auto', padding: '20px 0px' }}>
                <div className="container-fluid">
                    {/* Search and Add Button */}
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search users..."
                                    value={searchTerm}
                                    name='search'
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ height: '45px' }}
                                />
                                <span className="input-group-text">
                                    <i className="ti-search"></i>
                                </span>
                            </div>
                        </div>
                        <div className="col-md-6 text-end">
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowAddForm(true)}
                                style={{ height: '45px', padding: '0 24px' }}
                            >
                                <i className="ti-plus me-2"></i>Add New User
                            </button>
                        </div>
                    </div>

                    {/* Add User Form */}
                    {showAddForm && (
                        <div className="card mb-4" style={{ border: '1px solid #e5e7eb' }}>
                            <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
                                <h5 className="mb-0">Add New User</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label>First Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newUserData.firstname}
                                            onChange={(e) => setNewUserData({...newUserData, firstname: e.target.value})}
                                            style={{ height: '45px' }}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label>Last Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newUserData.lastname}
                                            onChange={(e) => setNewUserData({...newUserData, lastname: e.target.value})}
                                            style={{ height: '45px' }}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={newUserData.username}
                                            onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                                            style={{ height: '45px' }}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label>Password</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            autoComplete="new-password"
                                            value={newUserData.password}
                                            onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                                            style={{ height: '45px' }}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label>Role</label>
                                        <select
                                            className="form-control"
                                            value={newUserData.role_id}
                                            onChange={(e) => setNewUserData({...newUserData, role_id: e.target.value})}
                                            style={{ height: '45px' }}
                                        >
                                            <option value="">Select Role</option>
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>{role.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <button className="btn btn-success me-2" onClick={handleAddUser}>
                                        <i className="ti-check me-1"></i>Save User
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                                        <i className="ti-close me-1"></i>Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Users Table */}
                    <div className="card" style={{ border: '1px solid #e5e7eb' }}>
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden"></span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th style={{ padding: '15px' }}>Name</th>
                                                <th>Email</th>
                                                <th>Role</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.map(user => (
                                                <tr key={user.id}>
                                                    <td style={{ padding: '15px' }}>
                                                        {editingUser === user.id ? (
                                                            <div className="d-flex gap-2">
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={formData.firstname}
                                                                    onChange={(e) => setFormData({...formData, firstname: e.target.value})}
                                                                    placeholder="First Name"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={formData.lastname}
                                                                    onChange={(e) => setFormData({...formData, lastname: e.target.value})}
                                                                    placeholder="Last Name"
                                                                />
                                                            </div>
                                                        ) : (
                                                            `${user.firstname} ${user.lastname}`
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingUser === user.id ? (
                                                            <input
                                                                type="email"
                                                                className="form-control form-control-sm"
                                                                value={formData.username}
                                                                onChange={(e) => setFormData({...formData, username: e.target.value})}
                                                            />
                                                        ) : (
                                                            user.username
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingUser === user.id ? (
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={formData.role_id}
                                                                onChange={(e) => setFormData({...formData, role_id: e.target.value})}
                                                            >
                                                                {roles.map(role => (
                                                                    <option key={role.id} value={role.id}>{role.name}</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <span className="badge bg-info" >
                                                                {user.role_name || 'No Role'}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {editingUser === user.id ? (
                                                            <>
                                                                <button 
                                                                    className="btn btn-sm btn-success me-1"
                                                                    onClick={() => handleUpdate(user.id)}
                                                                >
                                                                    <i className="ti-check"></i>
                                                                </button>
                                                                <button 
                                                                    className="btn btn-sm btn-secondary"
                                                                    onClick={() => setEditingUser(null)}
                                                                >
                                                                    <i className="ti-close"></i>
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                               
                                                                <i className="ti-pencil" style={{marginRight: "20px", color:'#0c26f5'}} onClick={() => handleEdit(user)}></i>
                                                                
                                                                
                                                                <i className="ti-trash" style={{color:'#f00'}} onClick={() => setDeletingUser(user.id)}></i>
                                                                
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Delete Confirmation Modal */}
                    {deletingUser && (
                        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Confirm Delete</h5>
                                    </div>
                                    <div className="modal-body">
                                        Are you sure you want to delete this user?
                                    </div>
                                    <div className="modal-footer">
                                        <button 
                                            className="btn btn-danger"
                                            onClick={() => handleDelete(deletingUser)}
                                        >
                                            Delete
                                        </button>
                                        <button 
                                            className="btn btn-secondary"
                                            onClick={() => setDeletingUser(null)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ToastContainer />
        </Layout>
    );
};

export default UsersPage;